import { useCallback, useMemo } from "react";
import { axiosClient } from "../api/proxy/AxiosClient";

export function useLessonActions(role, setLessons, selectedLesson, setSelectedLesson, mode, setMode, confirm, isDemo=false, setChangedLesson = () => {}) {
  
  const updateLessonView = useCallback((lesson) => {
    setSelectedLesson(lesson);
    setMode("view");
  }, [ setSelectedLesson, setMode ]);

  const updateLessonAndList = useCallback(lessonResponse => {
    const updatedLesson = lessonResponse.data;
    setLessons((prev) => 
      prev.map((l) => (l.uid === updatedLesson.uid ? updatedLesson : l))
    );
    updateLessonView(updatedLesson);
    setChangedLesson(updatedLesson);
  }, [ updateLessonView, setLessons, setChangedLesson ]);

  const createLesson = async (newLesson, user) => {
    const payload =
      role === "student"
        ? {
            ...newLesson,
            studentUids: [user.uid],
            teacherUid: newLesson.teacher.uid,
          }
        : {
            ...newLesson,
            fee: parseFloat(newLesson.fee),
            studentUids: newLesson.students.map((s) => s.uid),
            teacherUid: user.uid,
          };

      axiosClient.post("/lessons", payload)
        .then(response => {
          const createdLesson = response.data
          setLessons((prev) => sortLessons([...prev, createdLesson]));
          updateLessonView(createdLesson);
        })
        .catch(err => console.log(err.message));
  };

  const editLesson = async (lessonUid, lessonData) => {
    const payload =
      role === "teacher"
        ? {
            ...lessonData,
            fee: lessonData.fee ? parseFloat(lessonData.fee) : undefined,
            studentUids: lessonData.students
              ? lessonData.students.map((s) => s.uid)
              : undefined,
          }
        : lessonData;

    axiosClient.patch(`/lessons/${lessonUid}`, payload)
      .then((response) => updateLessonAndList(response))
      .catch(err => console.log(err.message));
  };

  const rescheduleLesson = async (lessonUid, lessonData) => {
    axiosClient.post(`/lessons/${lessonUid}/reschedule`, lessonData)
      .then((response) => updateLessonAndList(response))
      .catch(err => console.log(err.message));
  };

  const basicActions = useMemo(() => {
    if (!selectedLesson) return [];

    const rescheduleAction = {
      label: "Reschedule",
      color: "#4B0082",// indigo
      active: () => !isDemo && selectedLesson && mode !== "reschedule",
      handler: () => setMode("reschedule"),
    };

    const moveAction = {
      label: "Reschedule",
      color: "#4B0082",// indigo
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => setMode("reschedule"),
    };

    const cancelAction = {
      label: "Cancel",
      color: "#FF0000",// red
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => {
        confirm(
          "Please, confirm Lesson cancellation!",
          "Are you sure you want to cancel this Lesson?",
          async () => {
            axiosClient.post(`/lessons/${selectedLesson.uid}/cancel`)
              .then((response) => updateLessonAndList(response))
              .catch(err => console.log(err.message));
          }
        )
      }
    };

    const rejectAction = {
      label: "Cancel",
      color: "#FF0000",// red
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => {
        confirm(
          "Please, confirm Lesson cancellation!",
          "Are you sure you want to cancel this Lesson?",
          async () => {
            axiosClient.post(`/lessons/${selectedLesson.uid}/cancel`)
              .then((response) => updateLessonAndList(response))
              .catch(err => console.log(err.message));
          }
        )
      }
    };

    const confirmAction = {
      label: "Confirm",
      color: "#006600", //green
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => {
        axiosClient.post(`/lessons/${selectedLesson.uid}/confirm`)
          .then((response) => updateLessonAndList(response))
          .catch(err => console.log(err.message));
      }
    };

    const deleteAction = {
      label: "Delete",
      color: "#FF0000",// red
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => {
        confirm(
          "Please, confirm Lesson deletion!",
          "Are you sure you want to delete this Lesson?",
          async () => {
            axiosClient.delete(`/lessons/${selectedLesson.uid}`)
              .then((response) => updateLessonAndList(response))
              .catch(err => console.log(err.message));
          }
        )
      }
    };

    const submitAction = {
      label: "Submit",
      color: "#006600",// green
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => {
        axiosClient.post(`/lessons/${selectedLesson.uid}/confirm`)
          .then((response) => updateLessonAndList(response))
          .catch(err => console.log(err.message));
      }
    };

    const billAction = {
      label: "Bill",
      color: "#006600",// green
      active: () => !isDemo,
      handler: () => {
        axiosClient.post(`/lessons/${selectedLesson.uid}/bill`)
          .then((response) => updateLessonAndList(response))
          .catch(err => console.log(err.message));
      }
    };

    const baseEdit = {
      label: "Edit",
      color: "#0000FF",// blue
      active: () => !isDemo && selectedLesson && mode === "view",
      handler: () => setMode("edit"),
    };

    const studentMap = {
      default: [],
      // the only 'final' and 'positive' statuses => "ongoing" => "finished"
      confirmed: [rescheduleAction, rejectAction],
      submitted: [rescheduleAction, rejectAction],
      ongoing: [],
      finished: [],
      // intermediate statuses made by Student's action
      requested: [rescheduleAction, rejectAction],
      rescheduled: [rescheduleAction, rejectAction],
      rejected: [deleteAction],
      // intermediate statuses waiting for Student's action
      proposed: [submitAction, rescheduleAction, rejectAction],
      moved: [submitAction, rescheduleAction, rejectAction],
      cancelled: [deleteAction],
      deleted: [],
      // billed
      billed: []
    };

    const teacherMap = {
      default: [],
      // the only 'final' and 'positive' statuses => "ongoing" => "finished"
      confirmed: [moveAction, cancelAction],
      submitted: [moveAction, cancelAction],
      ongoing: [],
      finished: [],
      // intermediate statuses made by Teacher's action
      proposed: [moveAction, cancelAction, billAction],
      moved: [moveAction, cancelAction, billAction],
      cancelled: [deleteAction],
      deleted: [],
      // intermediate statuses waiting for Teacher's action
      requested: [confirmAction, moveAction, cancelAction, billAction],
      rescheduled: [confirmAction, moveAction, cancelAction, billAction],
      rejected: [deleteAction],
      // billed
      billed: []
    };

    const actionMap = role === "student" ? studentMap : teacherMap;
    const extraBase = role === "teacher" ? [/*baseView,*/ baseEdit] : [];

    return [...extraBase, ...(actionMap[selectedLesson?.status || "default"])];
  }, [
    role,
    selectedLesson,
    mode,
    setMode,
    updateLessonAndList,
    confirm,
    isDemo
  ])  ;


  return {
    basicActions,
    createLesson,
    editLesson,
    rescheduleLesson,
    updateLessonAndList,
  };
}

const sortLessons = (lessons) =>
  lessons.sort((a, b) => new Date(b.startDttm) - new Date(a.startDttm));
